import React, { useState, useEffect } from "react";
import { VStack, useDisclosure } from "@chakra-ui/react";

// Import types
import {
  driverBoardTypes,
  recruitersTypes,
  processorsTypes,
} from "../../../types/Types";

// Import components
import ToolBar from "./scheduled/ToolBar";
import EditForm from "../shared/EditForm";
import ScheduleDataTable from "./scheduled/ScheduleDataTable";

// Import hooks
import useFetchProfileData from "../../../hooks/driverboard/fetchProfiledata";
import useFetchHistory from "../../../hooks/driverboard/fetchHistory";

interface props {
  userName: string;
  userEmail: string;
  userDepartment: string;
  searchText: string;
  selectedView: "All" | "Personal";
  data: driverBoardTypes[];
  isLoading: boolean;
  lastUpdated: Date | null;
  refresh: boolean;
  sortState: {
    column: keyof driverBoardTypes | null;
    direction: "asc" | "desc";
  };
  setSelectedView: (value: "All" | "Personal") => void;
  setSearchText: (value: string) => void;
  setRefresh: (value: boolean) => void;
  setSortState: (value: {
    column: keyof driverBoardTypes | null;
    direction: "asc" | "desc";
  }) => void;
  subTeam: "Tasha" | "Austin" | "All" | "Kia";
  setSubTeam: (value: "Tasha" | "Austin" | "All" | "Kia") => void;
  recruiters: recruitersTypes[];
  processors: processorsTypes[];
}

const Scheduled: React.FC<props> = ({
  userName,
  userEmail,
  userDepartment,
  searchText,
  selectedView,
  data,
  isLoading,
  lastUpdated,
  refresh,
  sortState,
  setSelectedView,
  setSearchText,
  setRefresh,
  setSortState,
  subTeam,
  setSubTeam,
  recruiters,
  processors,
}) => {
  const [selectedID, setSelectedID] = useState<number | null>(null);
  const [manualRefresh, setManualRefresh] = useState<boolean>(false);

  // Function to refresh profile data manually
  const refreshProfileData = () => {
    setManualRefresh(!manualRefresh);
  };

  // Select Division State
  const [selectedDivision, setSelectedDivision] = useState<
      "All Division" | "OTR" | "Dedicated" | "Student" | "Expedited"
  >("All Division");

  // Background filter
  const [backgroundCheckFilter, setBackgroundCheckFilter] = useState<
    "All" | "In-Progress" | "Completed" | "Manager Approval"
  >("All");

  // Registration filter
  const [selectedRegistration, setSelectedRegistration] =
    useState<string>("All");

  // Location filter
  const [selectedLocation, setSelectedLocation] = useState<string>("");

  // Cost Center filter
  const [selectedCostCenter, setSelectedCostCenter] = useState<string>("");

  // Selected Week
  const [selectedWeek, setSelectedWeek] = useState<string>(() => {
    const currentDate = new Date();
    const day = currentDate.getDay();
    const diff = currentDate.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(currentDate.setDate(diff));
    const currentYear = monday.getFullYear();
    const currentMonth = ("0" + (monday.getMonth() + 1)).slice(-2);
    const currentDay = ("0" + monday.getDate()).slice(-2);
    return `${currentMonth}/${currentDay}/${currentYear}`;
  });

  const {
    isOpen: isEditFormOpen,
    onOpen: onEditFormOpen,
    onClose: onEditFormClose,
  } = useDisclosure();

  // Fetch Versioning History
  const { historyLoading, changeLog } = useFetchHistory(
    selectedID,
    manualRefresh
  );

  // Fetch Profile
  const { profileLoading, profileData } = useFetchProfileData(
    selectedID,
    manualRefresh
  );

  // Function to handle row selection
  const handleSelect = (id: number) => {
    setSelectedID(id);
    onEditFormOpen();
  };

  // Function to close the edit form
  const handleEditFormClose = () => {
    onEditFormClose();
    setSelectedID(null);
  };

  // Recruiter Filter
  const [recruiterFilter, setRecruiterFilter] = useState<string>("");

  // Processor Filter
  const [processorFilter, setProcessorFilter] = useState<string>("");

  useEffect(() => {
    document.title = "Driver Board - Pipeline";
  }, []);

  return (
    <>
      <VStack
        width="calc(100% - 200px)"
        height="100%"
        background="white"
        shadow="md"
        borderRadius="2xl"
        padding="10px"
        alignItems="flex-start"
      >
        <ToolBar
          selectedView={selectedView}
          searchText={searchText}
          data={data}
          isLoading={isLoading}
          lastUpdated={lastUpdated}
          setSelectedView={setSelectedView}
          setSearchText={setSearchText}
          onRefresh={() => setRefresh(!refresh)}
          setSelectedDivision={setSelectedDivision}
          selectedDivision={selectedDivision}
          selectedWeek={selectedWeek}
          setSelectedWeek={setSelectedWeek}
          backgroundCheckFilter={backgroundCheckFilter}
          setBackgroundCheckFilter={setBackgroundCheckFilter}
          subTeam={subTeam}
          setSubTeam={setSubTeam}
          selectedRegistration={selectedRegistration}
          setSelectedRegistration={setSelectedRegistration}
          selectedLocation={selectedLocation}
          setSelectedLocation={setSelectedLocation}
          setCostCenter={setSelectedCostCenter}
          selectedCostCenter={selectedCostCenter}
          recruiters={recruiters}
          recruiterFilter={recruiterFilter}
          setRecruiterFilter={setRecruiterFilter}
          processors={processors}
          processorFilter={processorFilter}
          setProcessorFilter={setProcessorFilter}
        />
        <ScheduleDataTable
          selectedView={selectedView}
          subTeam={subTeam}
          data={data}
          selectedID={selectedID}
          selectedWeek={selectedWeek}
          selectedDivision={selectedDivision}
          sortState={sortState}
          handleSort={(column) => {
            if (sortState.column === column) {
              setSortState({
                column,
                direction: sortState.direction === "asc" ? "desc" : "asc",
              });
            } else {
              setSortState({
                column,
                direction: "asc",
              });
            }
          }}
          handleSelect={handleSelect}
          backgroundCheckFilter={backgroundCheckFilter}
          selectedRegistration={selectedRegistration}
          selectedLocation={selectedLocation}
          selectedCostCenter={selectedCostCenter}
          recruiterFilter={recruiterFilter}
          processorFilter={processorFilter}
        />
      </VStack>
      <EditForm
        userName={userName}
        userEmail={userEmail}
        userDepartment={userDepartment}
        isOpen={isEditFormOpen}
        onClose={handleEditFormClose}
        onRefresh={() => setRefresh(!refresh)}
        selectedID={selectedID}
        profileLoading={profileLoading}
        profileData={profileData}
        refreshProfileData={refreshProfileData}
        setSelectedID={setSelectedID}
        historyLoading={historyLoading}
        changeLog={changeLog}
      />
    </>
  );
};

export default Scheduled;
